import * as Dialog from '@radix-ui/react-dialog'
import { useState, useEffect } from 'react'
import { X, Loader2, Settings, SlidersHorizontal } from 'lucide-react'
import { CameraHttpSettingsSheet } from '@/components/cameras/CameraHttpSettingsSheet'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { camerasApi, cameraGroupsApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import type { Camera, CameraGroup } from '@/types'
import { LookupAddressButton } from '@/components/maps/LookupAddressButton'
import { SwitchRow } from '@/components/cameras/detection/SwitchRow'
import { DetectionConfig } from '@/components/cameras/detection/DetectionConfig'

interface CameraEditSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CameraEditSheet({ camera, open, onOpenChange }: CameraEditSheetProps) {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)  // #074 — audio settings are superuser-only

  // Form state — initialised from camera prop
  const [name, setName] = useState(camera.name)
  const [iccid, setIccid] = useState(camera.iccid ?? '')
  const [location, setLocation] = useState(camera.location ?? '')
  const [latitude, setLatitude] = useState(camera.latitude ?? '')
  const [longitude, setLongitude] = useState(camera.longitude ?? '')
  const [isActive, setIsActive] = useState(camera.is_active)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(camera.group_id)
  const [ingestionType, setIngestionType] = useState<'ftp' | 'http'>(camera.ingestion_type)
  const [pendingIngestionType, setPendingIngestionType] = useState<'ftp' | 'http' | null>(null)
  const [httpSettingsOpen, setHttpSettingsOpen] = useState(false)

  // Reset form when camera changes or sheet opens
  useEffect(() => {
    if (open) {
      setName(camera.name)
      setIccid(camera.iccid ?? '')
      setLocation(camera.location ?? '')
      setLatitude(camera.latitude ?? '')
      setLongitude(camera.longitude ?? '')
      setIsActive(camera.is_active)
      setSelectedGroupId(camera.group_id)
      setIngestionType(camera.ingestion_type)
      setPendingIngestionType(null)
    }
  }, [open, camera])

  // Fetch camera groups for the group selector
  const { data: cameraGroups = [] } = useQuery<CameraGroup[]>({
    queryKey: queryKeys.cameraGroups.all(),
    queryFn: () => cameraGroupsApi.getAll(),
    ...queryOptions.cameraGroups.all,
    enabled: open,
  })

  // Save mutation (handles both group membership and camera fields)
  const updateMutation = useMutation({
    mutationFn: async (payload: {
      cameraData: Parameters<typeof camerasApi.update>[1]
      newGroupId: number | null
    }) => {
      const { cameraData, newGroupId } = payload
      const previousGroupId = camera.group_id

      const groupOps: Promise<unknown>[] = []

      if (previousGroupId && previousGroupId !== newGroupId) {
        groupOps.push(cameraGroupsApi.removeCameras(previousGroupId, [camera.id]))
      }

      if (newGroupId && newGroupId !== previousGroupId) {
        groupOps.push(cameraGroupsApi.addCameras(newGroupId, [camera.id]))
      }

      if (groupOps.length) {
        await Promise.all(groupOps)
      }

      return camerasApi.update(camera.id, cameraData)
    },
    onSuccess: () => {
      toast.success(t('messages.saved'))
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      const detail = data?.detail
      const msg = typeof detail === 'string' ? detail : t('messages.saveFailed')
      toast.error(msg)
    },
  })

  const handleSave = () => {
    if (!name.trim()) {
      toast.error(t('messages.nameRequired'))
      return
    }
    updateMutation.mutate({
      cameraData: {
        name: name.trim(),
        iccid: iccid.trim() || undefined,
        location: location.trim() || undefined,
        latitude: latitude.trim() || null,
        longitude: longitude.trim() || null,
        is_active: isActive,
        ingestion_type: ingestionType,
      },
      newGroupId: selectedGroupId,
    })
  }

  const isSaving = updateMutation.isPending

  return (
    <>
    <Dialog.Root open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Slide-in panel from right */}
        <Dialog.Content
          className="fixed right-0 top-0 z-50 h-full w-full max-w-md border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <Settings className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">
                  {t('edit.title')}
                </Dialog.Title>
                <p className="text-xs text-muted-foreground truncate">{camera.name}</p>
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">

            {/* Identity */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t('edit.sectionIdentity')}
              </h3>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-name">{t('edit.name')}</label>
                <Input
                  id="cam-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('edit.namePlaceholder')}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-location">{t('edit.location')}</label>
                <Input
                  id="cam-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('edit.locationPlaceholder')}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('edit.coordinates')}</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder={t('edit.latPlaceholder')}
                    disabled={isSaving}
                  />
                  <Input
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder={t('edit.lngPlaceholder')}
                    disabled={isSaving}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t('edit.coordinatesHint')}
                </p>
                <LookupAddressButton
                  lat={latitude}
                  lng={longitude}
                  onAddress={(addr) => setLocation(addr)}
                  className="mt-2"
                  buttonLabel={t('edit.lookupAddress')}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-group">{t('edit.group')}</label>
                <Select
                  value={selectedGroupId === null ? 'none' : String(selectedGroupId)}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedGroupId(null)
                    } else {
                      setSelectedGroupId(Number(value))
                    }
                  }}
                  disabled={isSaving || cameraGroups.length === 0}
                >
                  <SelectTrigger id="cam-group">
                    <SelectValue placeholder={cameraGroups.length ? t('edit.selectGroup') : t('edit.noGroups')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('edit.noGroupOption')}</SelectItem>
                    {cameraGroups.map((group) => (
                      <SelectItem key={group.id} value={String(group.id)}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-iccid">{t('edit.iccid')}</label>
                <Input
                  id="cam-iccid"
                  value={iccid}
                  onChange={(e) => setIccid(e.target.value)}
                  placeholder={t('edit.iccidPlaceholder')}
                  className="font-mono text-sm"
                  disabled={isSaving}
                />
                <p className="text-xs text-muted-foreground">{t('edit.iccidHint')}</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-ingestion-type">{t('edit.ingestionType')}</label>
                <Select
                  value={ingestionType}
                  onValueChange={(value) => {
                    const next = value as 'ftp' | 'http'
                    if (next !== ingestionType) {
                      setPendingIngestionType(next)
                    }
                  }}
                  disabled={isSaving}
                >
                  <SelectTrigger id="cam-ingestion-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ftp">{t('edit.ingestionFtp')}</SelectItem>
                    <SelectItem value="http">{t('edit.ingestionHttp')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('edit.ingestionTypeHint')}</p>
              </div>

              {/* HTTP camera config push (Change #032) — only for cameras that
                  are PERSISTED as ingestion_type=http on the server.
                  We deliberately use camera.ingestion_type, not the local
                  ingestionType form state, so the button only shows once
                  the protocol switch has been saved. Otherwise the user
                  could draft a switch + save desired_* against an FTP
                  camera that will never POST /api/image to receive them. */}
              {camera.ingestion_type === 'http' && (
                <div className="space-y-1.5">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setHttpSettingsOpen(true)}
                    disabled={isSaving}
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    HTTP camera settings
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Push camera shooting parameters, schedule, storage, and one-shot commands to the camera firmware over HTTP.
                  </p>
                </div>
              )}

              {/* Read-only IMEI */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">{t('edit.imei')}</label>
                <div className="rounded-md bg-muted/50 px-3 py-2 font-mono text-sm text-muted-foreground select-all">
                  {camera.imei}
                </div>
              </div>
            </section>

            {/* Detection — owners tune per-class confidence on their own cameras (Phase 2);
                superusers get the full controls. */}
            {(isSuperuser || camera.is_owner) && (
              <DetectionConfig cameraId={camera.id} open={open} isSuperuser={!!isSuperuser} />
            )}

            {/* Camera Toggles */}
            <section className="space-y-1">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                {t('edit.sectionSettings')}
              </h3>
              <SwitchRow
                id="sw-active"
                label={t('edit.cameraActive')}
                description={t('edit.activeHint')}
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={isSaving}
              />
            </section>

          </div>

          {/* Footer */}
          <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              {tc('actions.cancel')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSaving ? t('edit.saving') : t('edit.saveChanges')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

    <ConfirmDialog
      open={pendingIngestionType !== null}
      onOpenChange={(o) => { if (!o) setPendingIngestionType(null) }}
      title={t('edit.ingestionConfirmTitle')}
      description={t('edit.ingestionConfirmBody', {
        newProtocol: pendingIngestionType === 'http' ? t('edit.ingestionHttp') : t('edit.ingestionFtp'),
      })}
      confirmLabel={t('edit.ingestionConfirmAction')}
      variant="destructive"
      onConfirm={() => {
        if (pendingIngestionType) setIngestionType(pendingIngestionType)
        setPendingIngestionType(null)
      }}
    />

    {/* HTTP camera settings sub-sheet (Change #032). Independent of the
        edit sheet save flow — the new sheet PATCHes the camera directly. */}
    <CameraHttpSettingsSheet
      camera={camera}
      open={httpSettingsOpen}
      onOpenChange={setHttpSettingsOpen}
    />
    </>
  )
}
